// Import from the source module rather than the `@/services/api` barrel: this
// file is pulled into the api-barrel <-> services/auth import cycle (via
// auth/hooks), and the barrel exports `hydraUiApi` after `servicesApi`, so going
// through the barrel can observe it before it's initialized.
import { z } from '@customerio/hydra-validation';
import { hydraUiApi } from '@/services/api/hydra-ui-api';
import { crossAppCache } from '@/utils/cross-app-cache';

/**
 * Account reads for the auth/session bootstrap, routed through
 * `hydraUiApi.accounts.*` while preserving the cross-app cache that the
 * `@customerio/hydra-authentication` session singleton seeds on boot and that
 * other embedded apps (Journeys UI, Pipelines) read.
 *
 * The current-account fetch hits the `/v1/accounts/:account_id` route with the
 * literal `account_id` "current"; the backend resolves it to the session's
 * account.
 */

const ACCOUNT_CACHE_TTL = 10000;

type AccountsGetResponse = Awaited<ReturnType<typeof hydraUiApi.accounts.get>>;

/**
 * The parsed-JSON body of the generated accounts response. The client types
 * every response body as a union (`string | Blob | Response | …`) because a
 * caller can ask for a raw body; these routes always read JSON.
 *
 * `account` and `user` stay optional, matching the spec: a not-yet-provisioned
 * account (which the signup readiness wait polls for) comes back without them,
 * and both `seedCurrentAccountCache` and the auth query handle absence.
 */
export type AccountResponse = Extract<AccountsGetResponse, { session_expires_at: number }>;

/**
 * The generated `accountResponse` describes the same backend payload as the old
 * ts-rest contract; the only thing to reconcile is the raw-or-parsed body union.
 * Ruling out the raw-body members is what narrows `data` to the parsed one — the
 * compiler does the narrowing, so nothing here is asserted.
 */
function toAccountResponse(data: AccountsGetResponse): AccountResponse {
  if (
    typeof data === 'string' ||
    data instanceof Response ||
    data instanceof Blob ||
    data instanceof FormData ||
    data instanceof ArrayBuffer
  ) {
    throw new Error('Account response body was not JSON');
  }

  return data;
}

/**
 * The cross-app cache is a genuinely unknown boundary: any bundle on the page
 * (the `hydra-authentication` session singleton, Journeys UI, Pipelines) can
 * have written the entry, at whatever version it shipped. The gate requires
 * only an object: the writers seed whole response payloads, but not every one
 * carries every member — `account_by_environment` responses have no
 * `session_expires_at`, for example — and rejecting such an entry would turn a
 * cache hit into a refetch of an endpoint the session already answered.
 */
const CachedAccountResponse = z.looseObject({}).pipe(z.custom<AccountResponse>());

/** A usable cached account payload, or `undefined` to make the caller fetch. */
function readCachedAccount(key: string): AccountResponse | undefined {
  const cached = CachedAccountResponse.safeParse(crossAppCache.getValue(key));
  return cached.success ? cached.data : undefined;
}

/**
 * Seed the current-account cross-app cache entries from a fetched payload.
 * `cacheOnlyOnce` means the first write wins for the TTL window — callers that
 * seed manually (the signup readiness wait) must only pass a payload they want
 * the auth bootstrap and the embedded apps to reuse.
 */
export function seedCurrentAccountCache(response: AccountResponse): void {
  if (response.account?.id == null) {
    return;
  }
  crossAppCache.setValue({ key: '/accounts/current', value: response, ttl: ACCOUNT_CACHE_TTL, cacheOnlyOnce: true });
  crossAppCache.setValue({
    key: `/accounts/${response.account.id}`,
    value: response,
    ttl: ACCOUNT_CACHE_TTL,
    cacheOnlyOnce: true,
  });
}

/**
 * Fetch the current account with no cache read *and no cache write*. For
 * callers polling for a state change (the signup readiness wait): reading the
 * cache would freeze the first response for the TTL window, and writing would
 * let a not-yet-ready payload win the `cacheOnlyOnce` slot. Seed explicitly
 * via `seedCurrentAccountCache` once the payload is the one to keep.
 */
export async function fetchCurrentAccountUncached(signal?: AbortSignal): Promise<AccountResponse> {
  return toAccountResponse(await hydraUiApi.accounts.get({ params: { path: { account_id: 'current' } }, signal }));
}

/** Fetch the session's current account, seeding the cross-app cache like the old `servicesApi.getAccount()`. */
export async function fetchCurrentAccount(): Promise<AccountResponse> {
  const cached = readCachedAccount('/accounts/current');
  if (cached != null) {
    return cached;
  }

  const response = await fetchCurrentAccountUncached();
  seedCurrentAccountCache(response);
  return response;
}

/** Fetch an account by ID, mirroring the cross-app cache behavior of the old `servicesApi.getAccountById()`. */
export async function fetchAccountById(accountId: number): Promise<AccountResponse> {
  const cached = readCachedAccount(`/accounts/${accountId}`);
  if (cached != null) {
    return cached;
  }

  const response = toAccountResponse(
    await hydraUiApi.accounts.get({ params: { path: { account_id: String(accountId) } } }),
  );

  if (response.account?.id != null) {
    crossAppCache.setValue({
      key: `/accounts/${accountId}`,
      value: response,
      ttl: ACCOUNT_CACHE_TTL,
      cacheOnlyOnce: true,
    });
  }

  return response;
}

/** Fetch an account by environment ID, mirroring the old `servicesApi.getAccountByEnvironmentId()`. */
export async function fetchAccountByEnvironment(environmentId: number): Promise<AccountResponse> {
  const cached = readCachedAccount(`/account_by_environment/${environmentId}`);
  if (cached != null) {
    return cached;
  }

  const response = toAccountResponse(
    await hydraUiApi.accounts.getAccountByEnvironment({ params: { path: { environment_id: String(environmentId) } } }),
  );

  if (response.account?.id != null) {
    crossAppCache.setValue({
      key: `/account_by_environment/${environmentId}`,
      value: response,
      ttl: ACCOUNT_CACHE_TTL,
      cacheOnlyOnce: true,
    });
    crossAppCache.setValue({
      key: `/accounts/${response.account.id}`,
      value: response,
      ttl: ACCOUNT_CACHE_TTL,
      cacheOnlyOnce: true,
    });
  }

  return response;
}
